import axios from "axios";

// eBay API configuration
const CORS_PROXY_BASE =
  "https://my-cors-anywhere24-3371cd77855e.herokuapp.com/";
const EBAY_API_DOMAIN = "https://api.ebay.com";
const EBAY_OAUTH_URL = `${EBAY_API_DOMAIN}/identity/v1/oauth2/token`;
const EBAY_SIGNIN_URL = "https://signin.ebay.com/ws/eBayISAPI.dll";
const EBAY_AUTHORIZATION_URL = "https://auth.ebay.com/oauth2/authorize";
const EBAY_TRADING_API_URL = "https://api.ebay.com/ws/api.dll"; // Trading API endpoint

let totalPages = 1;

// Create URLs with CORS proxy
const getProxiedUrl = (url) => `${CORS_PROXY_BASE}${url}`;

// eBay RuName (must be configured in your eBay developer account)
const RUNAME = process.env.REACT_APP_EBAY_RUNAME || "";

// eBay API credentials
const DEV_ID = process.env.REACT_APP_EBAY_DEV_ID || "";
const CERT_ID = process.env.REACT_APP_EBAY_CERT_ID || "";

/**
 * Get an application access token from eBay
 * This is the simplest authentication flow - no user consent required
 * @param {Object} credentials - { clientId, clientSecret }
 * @returns {Promise<Object>} Token response object
 */
export const getApplicationToken = async (credentials = {}) => {
  try {
    // Get credentials from parameters or environment variables
    const clientId =
      credentials.clientId || process.env.REACT_APP_EBAY_CLIENT_ID;
    const clientSecret =
      credentials.clientSecret || process.env.REACT_APP_EBAY_CLIENT_SECRET;

    if (!clientId || !clientSecret) {
      console.error("eBay API credentials not provided");
      throw new Error("eBay API credentials not provided");
    }

    // Create authorization string (Base64 encoded client_id:client_secret)
    const credentials64 = btoa(`${clientId}:${clientSecret}`);

    console.log("Requesting eBay application token...");

    // Construct the URL with CORS proxy
    const proxyUrl = getProxiedUrl(EBAY_OAUTH_URL);
    console.log("OAuth Request URL:", proxyUrl);

    // Make the token request
    const response = await axios.post(
      proxyUrl,
      "grant_type=client_credentials&scope=https://api.ebay.com/oauth/api_scope",
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Basic ${credentials64}`,
          "X-Requested-With": "XMLHttpRequest",
        },
        withCredentials: false,
      }
    );

    // Log token details (for debugging)
    console.log("Token obtained successfully!");
    console.log("Token type:", response.data.token_type);
    console.log("Expires in:", response.data.expires_in, "seconds");
    console.log("Scope:", response.data.scope);

    // Return the entire token response object
    return response.data;
  } catch (error) {
    // Log detailed error information
    console.error("Error getting application token:", error.message);

    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
      console.error("Response headers:", error.response.headers);
    }

    throw error;
  }
};

/**
 * Generate a session ID for the eBay authorization process
 * Uses the Trading API similar to the Java implementation
 * @param {Object} params - Parameters for session ID generation
 * @returns {Promise<string>} The session ID
 */
export const generateSessionId = async (params = {}) => {
  try {
    const { clientId, ruName, devId, certId } = params;

    // Get values from parameters or environment variables
    const effectiveClientId = clientId || process.env.REACT_APP_EBAY_CLIENT_ID;
    const effectiveRuName = ruName || RUNAME;
    const effectiveDevId = devId || DEV_ID;
    const effectiveCertId = certId || CERT_ID;

    if (!effectiveClientId) {
      throw new Error("eBay Client ID not provided");
    }

    if (!effectiveRuName) {
      throw new Error("eBay RuName not provided");
    }

    if (!effectiveDevId || !effectiveCertId) {
      throw new Error("eBay Dev ID or Cert ID not provided");
    }

    console.log("Generating eBay session ID via Trading API...");

    // Construct XML request following the Java implementation
    const xmlRequest = `<?xml version="1.0" encoding="utf-8"?>
<GetSessionIDRequest xmlns="urn:ebay:apis:eBLBaseComponents">
  <RuName>${effectiveRuName}</RuName>
</GetSessionIDRequest>`;

    // Construct the URL with CORS proxy
    const proxyUrl = getProxiedUrl(EBAY_TRADING_API_URL);

    // Make the Trading API request
    const response = await axios.post(proxyUrl, xmlRequest, {
      headers: {
        "Content-Type": "text/xml",
        "X-EBAY-API-SITEID": "0", // US site
        "X-EBAY-API-COMPATIBILITY-LEVEL": "967", // Use a recent compatibility level
        "X-EBAY-API-CALL-NAME": "GetSessionID",
        "X-EBAY-API-APP-NAME": effectiveClientId,
        "X-EBAY-API-DEV-NAME": effectiveDevId,
        "X-EBAY-API-CERT-NAME": effectiveCertId,
        "X-Requested-With": "XMLHttpRequest",
      },
      withCredentials: false,
    });

    // Parse XML response to extract session ID
    const responseText = response.data;
    console.log("Session ID Response:", responseText);

    // Extract SessionID from XML response using a simple regex
    // In a production app, use a proper XML parser
    const sessionIdMatch = responseText.match(/<SessionID>(.*?)<\/SessionID>/);
    if (sessionIdMatch && sessionIdMatch[1]) {
      const sessionId = sessionIdMatch[1];
      console.log("Session ID obtained:", sessionId);
      return sessionId;
    } else {
      throw new Error("Failed to extract SessionID from response");
    }
  } catch (error) {
    console.error("Error generating session ID:", error.message);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
    throw error;
  }
};

/**
 * Generate the eBay authorization URL for user consent using the SignIn URL format
 * @param {Object} params - Parameters including ruName and clientId
 * @returns {Promise<string>} The authorization URL to redirect the user to
 */
export const getAuthorizationUrl = async (params = {}) => {
  try {
    const { clientId, ruName, devId, certId } = params;

    // Get RuName from parameters or environment variables
    const effectiveRuName = ruName || RUNAME;
    const effectiveDevId = devId || DEV_ID;
    const effectiveCertId = certId || CERT_ID;

    if (!clientId) {
      throw new Error("eBay Client ID not provided");
    }

    if (!effectiveRuName) {
      throw new Error(
        "eBay RuName not provided. Please configure it in your .env file or pass it as a parameter."
      );
    }

    if (!effectiveDevId || !effectiveCertId) {
      throw new Error("eBay Dev ID or Cert ID not provided");
    }

    // Generate a session ID using Trading API
    const sessionId = await generateSessionId({
      clientId,
      ruName: effectiveRuName,
      devId: effectiveDevId,
      certId: effectiveCertId,
    });

    // Encode the session ID for the URL
    const encodedSessIDString = encodeURIComponent(sessionId);

    // Build the authorization URL using the SignIn URL format
    const authUrl = `${EBAY_SIGNIN_URL}?SignIn&RuName=${effectiveRuName}&SessID=${encodedSessIDString}`;

    // Store session data in session storage for later use when getting the token
    sessionStorage.setItem("ebay_session_id", sessionId);

    return authUrl;
  } catch (error) {
    console.error("Error generating authorization URL:", error.message);
    throw error;
  }
};
/**
 * Get a client token using the eBay Trading API approach (matching Java implementation)
 * This method should be called after the user has been redirected back from eBay
 * @param {Object} params - Parameters including sessionId, clientId, devId, and certId
 * @returns {Promise<Object>} The token response object
 */
export const getClientToken = async (params = {}) => {
  try {
    // For backward compatibility, check if we're using the old OAuth approach or new Trading API approach
    if (params.code) {
      console.warn(
        "Using deprecated OAuth code flow. Please update to use the Trading API approach."
      );
      // Call the old implementation (for backward compatibility)
      return getClientTokenOAuth(params);
    }

    const { sessionId, clientId, devId, certId } = params;

    // If no sessionId provided, try to get it from session storage
    const effectiveSessionId =
      sessionId || sessionStorage.getItem("ebay_session_id");

    if (!effectiveSessionId) {
      throw new Error(
        "No session ID provided or found in storage. Please authorize first."
      );
    }

    // Call the fetchToken function to get the token using the Trading API
    return await fetchToken({
      sessionId: effectiveSessionId,
      clientId,
      devId,
      certId,
    });
  } catch (error) {
    console.error("Error getting client token:", error.message);
    throw error;
  }
};

/**
 * Refresh a client access token using the refresh token
 * @param {Object} params - Parameters including the refresh token, client ID and secret
 * @returns {Promise<Object>} The token response object
 */
export const refreshClientToken = async (params = {}) => {
  try {
    const { refreshToken, clientId, clientSecret } = params;

    // Check required parameters
    if (!refreshToken || !clientId || !clientSecret) {
      throw new Error(
        "Missing required parameters: refreshToken, clientId, or clientSecret"
      );
    }

    // Create authorization string (Base64 encoded client_id:client_secret)
    const credentials64 = btoa(`${clientId}:${clientSecret}`);

    console.log("Refreshing eBay client token...");

    // Construct the URL with CORS proxy
    const proxyUrl = getProxiedUrl(EBAY_OAUTH_URL);

    // Prepare request body
    const requestBody = new URLSearchParams();
    requestBody.append("grant_type", "refresh_token");
    requestBody.append("refresh_token", refreshToken);

    // Make the token request
    const response = await axios.post(proxyUrl, requestBody.toString(), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Basic ${credentials64}`,
        "X-Requested-With": "XMLHttpRequest",
      },
      withCredentials: false,
    });

    // Log token details (for debugging)
    console.log("Client token successfully refreshed!");
    console.log("Token type:", response.data.token_type);
    console.log("Expires in:", response.data.expires_in, "seconds");

    // Store token in session storage for later use
    sessionStorage.setItem("ebay_client_token", response.data.access_token);

    // Return the entire token response object
    return response.data;
  } catch (error) {
    console.error("Error refreshing client token:", error.message);

    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }

    throw error;
  }
};

/**
 * Legacy method: Exchange an authorization code for a client access token using OAuth
 * This method is kept for backward compatibility
 * @param {Object} params - Parameters including the authorization code, client ID and secret
 * @returns {Promise<Object>} The token response object
 */
const getClientTokenOAuth = async (params = {}) => {
  try {
    const { code, clientId, clientSecret, ruName } = params;

    // Get RuName from parameters or environment variables
    const effectiveRuName = ruName || RUNAME;

    // Check required parameters
    if (!code || !clientId || !clientSecret || !effectiveRuName) {
      throw new Error(
        "Missing required parameters: code, clientId, clientSecret, or ruName"
      );
    }

    // Create authorization string (Base64 encoded client_id:client_secret)
    const credentials64 = btoa(`${clientId}:${clientSecret}`);

    console.log("Exchanging code for client access token...");

    // Construct the URL with CORS proxy
    const proxyUrl = getProxiedUrl(EBAY_OAUTH_URL);
    console.log("Token Request URL:", proxyUrl);

    // Prepare request body
    const requestBody = new URLSearchParams();
    requestBody.append("grant_type", "authorization_code");
    requestBody.append("code", code);
    requestBody.append("redirect_uri", effectiveRuName);

    // Make the token request
    const response = await axios.post(proxyUrl, requestBody.toString(), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Basic ${credentials64}`,
        "X-Requested-With": "XMLHttpRequest",
      },
      withCredentials: false,
    });

    // Log token details (for debugging)
    console.log("Client token obtained successfully!");
    console.log("Token type:", response.data.token_type);
    console.log("Expires in:", response.data.expires_in, "seconds");
    console.log("Scope:", response.data.scope);

    // Store token in session storage for later use
    sessionStorage.setItem("ebay_client_token", response.data.access_token);

    // Return the entire token response object
    return response.data;
  } catch (error) {
    // Log detailed error information
    console.error("Error getting client token:", error.message);

    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
      console.error("Response headers:", error.response.headers);
    }

    throw error;
  }
};

/**
 * Fetch a client token using the eBay Trading API (FetchToken call)
 * This matches the Java implementation's approach
 * @param {Object} params - Parameters including sessionId, clientId, devId, and certId
 * @returns {Promise<Object>} The token response object with access_token
 */
const fetchToken = async (params = {}) => {
  try {
    const { sessionId, clientId, devId, certId } = params;

    // Get values from parameters or environment variables
    const effectiveClientId = clientId || process.env.REACT_APP_EBAY_CLIENT_ID;
    const effectiveDevId = devId || DEV_ID;
    const effectiveCertId = certId || CERT_ID;

    // Check required parameters
    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    if (!effectiveClientId || !effectiveDevId || !effectiveCertId) {
      throw new Error("Missing required eBay API credentials");
    }

    console.log("Fetching eBay token via Trading API...");

    // Construct XML request following the Java implementation
    const xmlRequest = `<?xml version="1.0" encoding="utf-8"?>
<FetchTokenRequest xmlns="urn:ebay:apis:eBLBaseComponents">
  <SessionID>${sessionId}</SessionID>
  <WarningLevel>High</WarningLevel>
</FetchTokenRequest>`;

    // Construct the URL with CORS proxy
    const proxyUrl = getProxiedUrl(EBAY_TRADING_API_URL);

    // Make the Trading API request
    const response = await axios.post(proxyUrl, xmlRequest, {
      headers: {
        "Content-Type": "text/xml",
        "X-EBAY-API-SITEID": "0", // US site
        "X-EBAY-API-COMPATIBILITY-LEVEL": "967", // Use a recent compatibility level
        "X-EBAY-API-CALL-NAME": "FetchToken",
        "X-EBAY-API-APP-NAME": effectiveClientId,
        "X-EBAY-API-DEV-NAME": effectiveDevId,
        "X-EBAY-API-CERT-NAME": effectiveCertId,
        "X-Requested-With": "XMLHttpRequest",
      },
      withCredentials: false,
    });

    // Parse XML response to extract token
    const responseText = response.data;
    console.log("FetchToken Response:", responseText);

    // Extract eBayAuthToken from XML response using a simple regex
    // In a production app, use a proper XML parser
    const tokenMatch = responseText.match(
      /<eBayAuthToken>(.*?)<\/eBayAuthToken>/s
    );
    const expirationMatch = responseText.match(
      /<HardExpirationTime>(.*?)<\/HardExpirationTime>/s
    );

    if (tokenMatch && tokenMatch[1]) {
      const token = tokenMatch[1];
      const expiration =
        expirationMatch && expirationMatch[1] ? expirationMatch[1] : null;

      console.log("Token obtained successfully!");
      console.log("Token expiration:", expiration);

      // Store token in session storage for later use
      sessionStorage.setItem("ebay_client_token", token);
      if (expiration) {
        sessionStorage.setItem("ebay_token_expiration", expiration);
      }

      // Return in a format similar to OAuth response for compatibility
      return {
        access_token: token,
        token_type: "User",
        expires_in: expiration ? new Date(expiration) - new Date() : null,
        expiration_time: expiration,
      };
    } else {
      throw new Error("Failed to extract token from response");
    }
  } catch (error) {
    console.error("Error fetching token:", error.message);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
    throw error;
  }
};
/**
 * Fetch eBay listings using the Trading API with a client token
 * @param {Object} credentials - Object containing token, clientId, devId, and certId
 * @param {string} startDateFilter - Start date filter
 * @param {string} startDateToFilter - End date filter
 * @param {number} pageCount - Number of pages to fetch (200 items per page)
 * @returns {Promise<Array>} Array of listing objects
 */
export const fetchMyEbayListings = async (
  credentials = {},
  startDateFilter,
  startDateToFilter,
  pageCount
) => {
  try {
    // Get token from credentials or from session storage
    const token =
      credentials.token || sessionStorage.getItem("ebay_client_token");
    const clientId =
      credentials.clientId || process.env.REACT_APP_EBAY_CLIENT_ID;
    const devId = credentials.devId || process.env.REACT_APP_EBAY_DEV_ID;
    const certId = credentials.certId || process.env.REACT_APP_EBAY_CERT_ID;

    if (!token) {
      throw new Error(
        "eBay token is required. Please authorize with eBay first."
      );
    }

    if (!clientId || !devId || !certId) {
      throw new Error("Missing required eBay API credentials");
    }

    console.log("Fetching eBay listings via Trading API...");

    // Get current date and date 30 days ago for the date range
    // let startDate = startDateFilter === "all" ? new Date() : new Date(startDateFilter);
    // if(startDateFilter === "all") {
    //   startDate.setDate(startDate.getDate() - 120)
    // }
    // const startDateTo = startDateToFilter === "all" ? new Date() : new Date(startDateToFilter);

    // // Format dates for eBay API (ISO format)
    // const startDateStr = startDate.toISOString();
    // const startDateToStr = startDateTo.toISOString();

    // console.log("StartDate:", startDateStr);
    // console.log("StartDateTo:", startDateToStr);
    // console.log("Fetching", pageCount, "pages");

    let allListings = [];

    // Fetch listings for each page
    for (let page = 1; page <= 1; page++) {
      page === 1
        ? console.log(`Fetching page ${page} ...`)
        : console.log(`Fetching page ${page} of ${totalPages}`);

      const API_CALL_NAME = "GetMyeBaySelling";

      const xmlRequest = `
        <?xml version="1.0" encoding="utf-8"?>
        <${API_CALL_NAME}Request xmlns="urn:ebay:apis:eBLBaseComponents">
          <RequesterCredentials>
            <eBayAuthToken>${token}</eBayAuthToken>
          </RequesterCredentials>
          <ErrorLanguage>en_US</ErrorLanguage>
          <WarningLevel>High</WarningLevel>
          
          <ActiveList>
            <Include>true</Include>
            <Pagination>
              <EntriesPerPage>200</EntriesPerPage>
              <PageNumber>${page}</PageNumber>
            </Pagination>
            <Sort>TimeLeft</Sort>
          </ActiveList>
          
          <DetailLevel>ReturnAll</DetailLevel>
        </${API_CALL_NAME}Request>
        `;

      // Construct the URL with CORS proxy
      const proxyUrl = getProxiedUrl(EBAY_TRADING_API_URL);

      // Make the Trading API request
      const response = await axios.post(proxyUrl, xmlRequest, {
        headers: {
          "Content-Type": "text/xml",
          "X-EBAY-API-SITEID": "0", // US site
          "X-EBAY-API-COMPATIBILITY-LEVEL": "967", // Use a recent compatibility level
          "X-EBAY-API-CALL-NAME": API_CALL_NAME,
          "X-EBAY-API-APP-NAME": clientId,
          "X-EBAY-API-DEV-NAME": devId,
          "X-EBAY-API-CERT-NAME": certId,
          "X-Requested-With": "XMLHttpRequest",
        },
        withCredentials: false,
      });

      // Parse XML response
      const responseText = response.data;
      // console.log("Response received, length:", responseText.length);

      // Check for errors
      const errorMatch = responseText.match(/<Errors>.*?<\/Errors>/gs);
      if (errorMatch) {
        console.error("eBay API Error:", errorMatch[0]);
        throw new Error("eBay API returned an error");
      }

      // Extract pagination info
      if (page === 1) {
        const totalPagesMatch = responseText.match(
          /<TotalNumberOfPages>(.*?)<\/TotalNumberOfPages>/s
        );
        totalPages = totalPagesMatch[1];
        console.log("TotalPages set to " + totalPages);
        const totalEntriesMatch = responseText.match(
          /<TotalNumberOfEntries>(.*?)<\/TotalNumberOfEntries>/s
        );
        console.log(
          "Total pages:",
          totalPagesMatch ? totalPagesMatch[1] : "Not found"
        );
        console.log(
          "Total entries:",
          totalEntriesMatch ? totalEntriesMatch[1] : "Not found"
        );
      }

      // Extract items from XML response using regex
      // In a production app, use a proper XML parser
      const itemMatches = responseText.match(/<Item>.*?<\/Item>/gs);

      if (itemMatches) {
        const pageListings = itemMatches.map((itemXml) => {
          // Extract listing details using regex
          const titleMatch = itemXml.match(/<Title>(.*?)<\/Title>/s);
          const itemIdMatch = itemXml.match(/<ItemID>(.*?)<\/ItemID>/s);
          const startTimeMatch = itemXml.match(
            /<StartTime>(.*?)<\/StartTime>/s
          );
          const endTimeMatch = itemXml.match(/<EndTime>(.*?)<\/EndTime>/s);
          const currentPriceMatch = itemXml.match(
            /<CurrentPrice currencyID="([^"]+)">(.*?)<\/CurrentPrice>/s
          );
          const quantityMatch = itemXml.match(/<Quantity>(.*?)<\/Quantity>/s);
          const quantitySoldMatch = itemXml.match(
            /<QuantitySold>(.*?)<\/QuantitySold>/s
          );
          const listingStatusMatch = itemXml.match(
            /<ListingStatus>(.*?)<\/ListingStatus>/s
          );
          const watchCountMatch = itemXml.match(
            /<WatchCount>(.*?)<\/WatchCount>/s
          );
          const skuMatch = itemXml.match(/<SKU>(.*?)<\/SKU>/s);
          const siteMatch = itemXml.match(/<Site>(.*?)<\/Site>/s);
          const pictureDetailsMatch = itemXml.match(
            /<PictureDetails>.*?<\/PictureDetails>/s
          );
          const pictureURLMatch = pictureDetailsMatch
            ? pictureDetailsMatch[0].match(/<PictureURL>(.*?)<\/PictureURL>/s)
            : null;
          const viewItemURLMatch = itemXml.match(
            /<ViewItemURL>(.*?)<\/ViewItemURL>/s
          );

          return {
            id: itemIdMatch ? itemIdMatch[1] : null,
            title: titleMatch ? titleMatch[1] : null,
            startTime: startTimeMatch ? startTimeMatch[1] : null,
            endTime: endTimeMatch ? endTimeMatch[1] : null,
            price: currentPriceMatch ? currentPriceMatch[2] : null,
            currency: currentPriceMatch ? currentPriceMatch[1] : null,
            quantity: quantityMatch ? parseInt(quantityMatch[1]) : null,
            quantitySold: quantitySoldMatch
              ? parseInt(quantitySoldMatch[1])
              : 0,
            status: listingStatusMatch ? listingStatusMatch[1] : null,
            watchCount: watchCountMatch ? parseInt(watchCountMatch[1]) : 0,
            sku: skuMatch ? skuMatch[1] : null,
            country: siteMatch ? siteMatch[1] : null,
            imageUrl: pictureURLMatch ? pictureURLMatch[1] : null,
            url: viewItemURLMatch ? viewItemURLMatch[1] : null,
          };
        });

        allListings = [...allListings, ...pageListings];
      }

      // Add a small delay between requests to avoid rate limiting
      if (page < totalPages) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    // Deduplicate by listing.id
    const uniqueListings = [];
    const seenIds = new Set();
    for (const listing of allListings) {
      if (!seenIds.has(listing.id)) {
        uniqueListings.push(listing);
        seenIds.add(listing.id);
      }
    }

    return uniqueListings;
  } catch (error) {
    console.error("Error fetching eBay listings:", error.message);

    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }

    throw error;
  }
};
